import { useEffect, useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { OtpInput } from '../components/common/OtpInput'
import { roleStartPath } from '../utils/constants'
import { brand } from '@/ui/brand'

/** Screen A (E-Mail) vs. Screen B (OTP-Eingabe). Rein passwortlos — kein
 *  Passwort-Feld, kein „Passwort vergessen?" (das gibt es nur im Admin-Login). */
type Stage = 'idle' | 'otp'

/** Cooldown für „Code erneut senden" (Spec §3.2). */
const RESEND_COOLDOWN_SECONDS = 30

/** Übersetzt einen fehlgeschlagenen Auth-Request in eine sprechende Fehlermeldung (Spec §3.2). */
function authErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    if (status === 401) return 'E-Mail/Passwort ungültig'
    if (status === 400) return 'Code ungültig'
    if (status === 429) return 'Zu viele Versuche, bitte später erneut.'
  }
  return 'Etwas ist schiefgelaufen, bitte erneut versuchen.'
}

/**
 * Editor-Login (`/editor/login`, F2 Split): reiner OTP-Flow für die Rolle
 * `user` — E-Mail → Code anfordern → OTP bestätigen. Kein Passwort, kein
 * „vertrautes Gerät", kein Passwort-Reset. Lehnt das Backend den Request ab
 * (z. B. ein admin-Konto ohne OTP-only-Unterstützung), zeigt die generische
 * Fehlermeldung — kein Crash, Backend ist maßgeblich.
 * BMC/shadcn-Optik (Light+Dark), Auth-Flow unverändert (OTP/`verifyOtp`).
 */
export function EditorLoginPage() {
  const { session, status, requestOtp, verifyOtp } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [stage, setStage] = useState<Stage>('idle')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  // Redirect nach erfolgreichem Login — `from` aus dem RequireAuth-Redirect
  // hat Vorrang vor der rollenrichtigen Startseite.
  useEffect(() => {
    if (status !== 'authed' || !session) return
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname
    navigate(from || roleStartPath(session.role), { replace: true })
  }, [status, session, location.state, navigate])

  // Cooldown-Countdown für den „Code erneut senden"-Button.
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setSubmitting(true)
    requestOtp(email)
      .then(() => {
        setStage('otp')
        setCooldown(RESEND_COOLDOWN_SECONDS)
      })
      .catch((err: unknown) => setError(authErrorMessage(err)))
      .finally(() => setSubmitting(false))
  }

  const handleVerify = () => {
    setError('')
    setSubmitting(true)
    verifyOtp({ email, code })
      .catch((err: unknown) => setError(authErrorMessage(err)))
      .finally(() => setSubmitting(false))
  }

  const handleResend = () => {
    if (cooldown > 0) return
    setError('')
    requestOtp(email)
      .then(() => setCooldown(RESEND_COOLDOWN_SECONDS))
      .catch((err: unknown) => setError(authErrorMessage(err)))
  }

  const handleCancel = () => {
    setStage('idle')
    setCode('')
    setError('')
  }

  const logo = theme === 'dark' ? brand.logoDark : brand.logoLight

  return (
    <div className="login">
      <div className="login-card">
        <div className="lbrand">
          <img src={logo} alt={brand.name} className="brand-logo" />
        </div>
        <div className="eyebrow">vCard-Editor</div>
        <h1>
          {brand.name} <span className="text-primary">{brand.product}</span>
        </h1>

        {stage === 'idle' && (
          <form className="loginform" onSubmit={handleSubmit}>
            <div className="field">
              <label className="flabel" htmlFor="login-email">
                E-Mail
              </label>
              <input
                className="inp"
                id="login-email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
            )}

            <button type="submit" className="btn primary block" disabled={submitting || !email}>
              Code anfordern
            </button>
          </form>
        )}

        {stage === 'otp' && (
          <div className="loginform">
            <p className="text-sm text-muted-foreground">Code an {email} gesendet.</p>
            <OtpInput value={code} onChange={setCode} />

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
            )}

            <button
              type="button"
              className="btn primary block"
              disabled={submitting || code.length < 6}
              onClick={handleVerify}
            >
              Bestätigen
            </button>

            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                className="text-sm font-medium text-primary hover:underline disabled:opacity-45"
                disabled={cooldown > 0}
                onClick={handleResend}
              >
                {cooldown > 0 ? `Code erneut senden (${cooldown}s)` : 'Code erneut senden'}
              </button>
              <button
                type="button"
                className="text-sm font-medium text-primary hover:underline"
                onClick={handleCancel}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
